import { useState } from "react";
import axios from "axios";
import { Linkedin, Phone, Mail, CheckCircle2, Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { CONTACT, CONTACTS } from "@/data/content";

export default function Contact() {
  const emptyForm = {
    name: "",
    company: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      setSubmitted(true);
      setForm(emptyForm);
    } catch (err) {
      setError("A apărut o eroare la trimitere. Te rugăm să încerci din nou.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-ink placeholder:text-slate-400 outline-none transition-colors focus:border-brand focus:ring-4 focus:ring-brand/15";

  return (
    <div data-testid="contact-page">
      <PageHero kicker={CONTACT.kicker} title={CONTACT.title} intro={CONTACT.intro} variant="cool" />

      <section className="py-12 md:py-16">
        <div className="container-x grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          {/* TEAM */}
          <div className="space-y-6">
            {CONTACTS.map((p, i) => (
              <Reveal key={p.email} delay={i * 0.1}>
                <div className="card flex items-center gap-5 p-5 md:p-6" data-testid={`contact-person-${i}`}>
                  <img src={p.photo} alt={p.name} className="h-20 w-20 shrink-0 rounded-2xl object-cover" />
                  <div className="min-w-0">
                    <h3 className="text-lg font-bold tracking-tight">{p.name}</h3>
                    <a
                      href={`tel:${p.phone.replace(/[.\s]/g, "")}`}
                      className="mt-2 flex items-center gap-2 text-sm text-slate-600 hover:text-brand transition-colors"
                    >
                      <Phone className="h-4 w-4" /> {p.phone}
                    </a>
                    <a
                      href={`mailto:${p.email}`}
                      className="mt-1 flex items-center gap-2 text-sm text-slate-600 hover:text-brand transition-colors break-all"
                    >
                      <Mail className="h-4 w-4" /> {p.email}
                    </a>
                    <a
                      href={p.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-brand transition-colors"
                    >
                      <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* FORM */}
          <Reveal delay={0.12}>
            {submitted ? (
              <div className="card p-8 md:p-12 text-center" data-testid="contact-success">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand/10">
                  <CheckCircle2 className="h-8 w-8 text-brand" />
                </div>
                <h2 className="mt-6 text-2xl md:text-3xl font-bold tracking-tight">Mulțumim!</h2>
                <p className="lead mt-3">Ți-am primit mesajul și te contactăm în curând.</p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-send-another"
                  className="btn-secondary mt-7"
                >
                  Trimite un alt mesaj
                </button>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="card p-7 md:p-10" data-testid="contact-form">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{CONTACT.formTitle}</h2>
              <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    {CONTACT.fields.name} <span className="text-brand">*</span>
                  </label>
                  <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name" />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    {CONTACT.fields.company} <span className="text-brand">*</span>
                  </label>
                  <input required value={form.company} onChange={set("company")} className={inputCls} data-testid="contact-company" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    {CONTACT.fields.email} <span className="text-brand">*</span>
                  </label>
                  <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    {CONTACT.fields.phone}
                  </label>
                  <input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone" />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    {CONTACT.fields.interest} <span className="text-brand">*</span>
                  </label>
                  <select
                    required
                    value={form.interest}
                    onChange={set("interest")}
                    className={inputCls + " appearance-none cursor-pointer"}
                    data-testid="contact-interest"
                  >
                    <option value="" disabled>
                      Alege o opțiune
                    </option>
                    {CONTACT.interestOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    {CONTACT.fields.message}
                  </label>
                  <textarea
                    rows={6}
                    maxLength={2000}
                    value={form.message}
                    onChange={set("message")}
                    className={inputCls + " resize-none"}
                    data-testid="contact-message"
                  />
                </div>
              </div>
              {error && (
                <p className="mt-4 text-sm font-medium text-red-600" data-testid="contact-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="btn-primary mt-2 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Se trimite...
                  </>
                ) : (
                  CONTACT.submit
                )}
              </button>
            </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
